import { useState } from "react";
import { useBase } from "@/contexts/BaseContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Inline control to view / change the base a record belongs to.
 * - All-access users (admin/master/executive) get a Select to move the
 *   record to another base ("one-tap" transfer / bot reassignment).
 * - Everyone else sees a read-only badge.
 *
 * Props:
 *   value     — current base id
 *   onAssign  — async (baseId) => void   (performs the PATCH)
 *   size      — "sm" (default) | "xs"
 */
export function BaseAssignControl({ value, onAssign, size = "sm", testid }) {
  const { bases, allAccess } = useBase();
  const [saving, setSaving] = useState(false);
  const current = bases.find((b) => b.id === value);

  if (!allAccess || bases.length <= 1) {
    return (
      <Badge variant="outline" className="gap-1 font-normal" data-testid={testid ? `${testid}-badge` : undefined}>
        <MapPin className="h-3 w-3 text-[hsl(var(--ap-forest))]" />
        {current?.name || "—"}
      </Badge>
    );
  }

  const handle = async (next) => {
    if (!next || next === value) return;
    setSaving(true);
    try {
      await onAssign(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select value={value || ""} onValueChange={handle} disabled={saving}>
      <SelectTrigger
        data-testid={testid}
        aria-label="Assign base"
        className={size === "xs" ? "h-7 w-auto min-w-[110px] gap-1 px-2 text-xs" : "h-8 w-auto min-w-[130px] gap-1 px-2 text-sm"}
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 text-[hsl(var(--ap-forest))]" />}
        <SelectValue placeholder="Set base" />
      </SelectTrigger>
      <SelectContent>
        {bases.map((b) => (
          <SelectItem key={b.id} value={b.id} data-testid={testid ? `${testid}-opt-${b.code || b.id}` : undefined}>
            {b.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
